import { TriangleDownIcon, XCircleFillIcon } from '@primer/octicons-react';
import { Box, Button, SelectPanel } from '@primer/react';
import { useEffect, useMemo, useState } from 'react';
import { lopMonHocApi } from '../../api/lopMonHocApi';

type SubjectItem = {
    id: number;
    name: string;
    sis_giaovien_id: number;
};

type Props = {
    lopId: number;
    selectedSubjects: SubjectItem[];
    value: number;
    onValueChanged: (id: number, data?: SubjectItem) => void;
};

const ComboboxLopMonHoc = ({ lopId, selectedSubjects, value, onValueChanged }: Props) => {
    const [subjects, setSubjects] = useState<SubjectItem[]>([]);
    const [filter, setFilter] = useState('');
    const [open, setOpen] = useState(false);

    // ✅ call API khi có lopId
    useEffect(() => {
        const fetchData = async () => {
            if (!lopId) return;

            const res = await lopMonHocApi.SelectByLop(lopId);
            const data = await res.data;

            if (data) {
                setSubjects(
                    data.map((x: any) => ({
                        id: x.id,
                        name: x.ten_mon,
                        sis_giaovien_id: x.sis_giaovien_id
                    }))
                );
            }
        };

        fetchData();
    }, [lopId]);

    // ✅ lọc bỏ môn đã chọn
    const availableSubjects = useMemo(() => {
        return subjects.filter(
            s => !selectedSubjects.some(ss => ss.id === s.id)
        );
    }, [subjects, selectedSubjects]);

    const dataSource = useMemo(() => {
        return availableSubjects.map(x => ({
            id: x.id,
            text: x.name
        }));
    }, [availableSubjects]);

    const filteredData = useMemo(() => {
        return dataSource.filter(item =>
            item.text.toLowerCase().includes(filter.toLowerCase())
        );
    }, [dataSource, filter]);

    const selectedData = useMemo(() => {
        return dataSource.find(x => x.id === value);
    }, [value, dataSource]);

    return (
        <SelectPanel
            renderAnchor={({ children, ...anchorProps }) => (
                <Button trailingAction={TriangleDownIcon} {...anchorProps}>
                    {children || "Chọn bộ môn"}
                </Button>
            )}
            title={
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Box sx={{ flex: 1 }}>Chọn bộ môn</Box>

                    {value > 0 && (
                        <Button
                            trailingVisual={XCircleFillIcon}
                            variant="invisible"
                            onClick={() => onValueChanged(0)}
                        >
                            Bỏ chọn
                        </Button>
                    )}
                </Box>
            }
            placeholderText="Search subject..."
            open={open}
            onOpenChange={setOpen}
            items={filteredData}
            selected={selectedData}
            onSelectedChange={(item: any) => {
                if (item) {
                    const subject = subjects.find(x => x.id === item.id);
                    onValueChanged(item.id, subject);
                }
            }}
            onFilterChange={setFilter}
            showItemDividers
            overlayProps={{ width: 'medium', height: 'medium' }}
        />
    );
};

export default ComboboxLopMonHoc;